import { Component, OnInit, Input, ViewChild } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { AgGridAngular } from 'ag-grid-angular';
import { GridOptions } from 'ag-grid-enterprise';
import { ChallengesService } from 'src/app/gamification/command-center/services/challenges.service';
import { AgGridHelperService } from 'src/app/reusables/services/ag-grid-helper.service';
import { ExportItems, TypeFormats } from 'src/shared/types/exportItem';
import { ChallengeTypes, ChallengeTypeValues } from '../../../models/challenge.model';
import { MenuItem } from '@niceltd/sol/floating-menu/src/lib/menu-items';
import moment from 'moment';

@Component({
    selector: 'cv-challenged-users-modal',
    templateUrl: './challenged-users-modal.component.html',
    styleUrls: ['./challenged-users-modal.component.scss']
})
export class ChallengedUsersModalComponent implements OnInit {
    @Input() challengeObj: any;
    @Input() isCxone: boolean = false;
    @ViewChild('challengeUsersModalGrid') agGrid: AgGridAngular;

    cs = null;
    usersDetails: GridOptions = null;
    exportOptions: ExportItems = {
        excel: {
            getData: async (format) => this.getUserDetailData(format),
        },
        csv: {
            getData: async (format) => this.getUserDetailData(format),
        }
    };
    defaultMenuOptions: MenuItem[] = [
        {
            label: "Excel",
            svgIconName: "icon-office-excel",
            callbackFn: (item: MenuItem) => this.onExportClick(item),
        },
        {
            label: "CSV",
            svgIconName: "icon-CSV",
            callbackFn: (item: MenuItem) => this.onExportClick(item),
        }
    ];
    

    constructor(
        private activeModalService: NgbActiveModal,
        private challengesService: ChallengesService,
        private agGridHelperService: AgGridHelperService
    ) {
        this.cs = this.challengesService.data;
    }

    async onExportClick(menuItem: MenuItem) {
        const format = menuItem.label === 'Excel' ? 'excel' : 'csv';
        const data = await this.getUserDetailData(format);

        let mimeType = '';
        let extension = '';
    
        if (format === 'excel') {
            mimeType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
            extension = 'xlsx';
        } else {
            mimeType = 'text/csv';
            extension = 'csv';
        }
    
        const currentDate: string = moment().format('DD MMM, YYYY h:mm:ss A');
        const formattedDate: string[] = currentDate.split(' ');
        const formattedDateTime: string[] = formattedDate[3].split(':');
        const dateTimeSplitted: string = formattedDateTime[0] + 'h' + formattedDateTime[1] + 'm' + formattedDateTime[2] + 's ';
        const filename = `ChallengeDetails_` + formattedDate[0] + ' ' + formattedDate[1] + formattedDate[2] + ' ' + dateTimeSplitted + formattedDate[4] + `.${extension}`;
        this.downloadFile(data, mimeType, filename);
    }
    
    downloadFile(data: string, mime: string, fileName: string) {
        const decodedData = atob(data);
        const byteArray = new Uint8Array(decodedData.length);
    
        for (let i = 0; i < decodedData.length; i++) {
            byteArray[i] = decodedData.charCodeAt(i);
        }
    
        const blob = new Blob([byteArray], { type: mime });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = fileName;
        link.click();
    
        URL.revokeObjectURL(link.href);
    }    
    
    ngOnInit(): void {
        this.setupGrid();
        this.loadGrid();
    }

    setupGrid(): void {
        this.usersDetails = this.agGridHelperService.createGridOptions({
            columnDefs: [],
            rowData: [],
            animateRows: true,
            suppressContextMenu: true,
            suppressCellFocus: true,
            suppressRowHoverHighlight: true,
            suppressDragLeaveHidesColumns: true,

            components: {
                groupRenderer: this.agGridHelperService.GroupDrillDownCellRenderer,
            },
            rowHeight: 42,
            defaultColDef: {
                sortable: true,
                filter: true,
                resizable: true,
                menuTabs: ['filterMenuTab', 'generalMenuTab'], // 'columnsMenuTab'
            },
            onGridSizeChanged: (e) => {
                e.api.sizeColumnsToFit();
            },
            getRowId: ({ data: rowData }) => {
                return rowData.LoginID;
            }
        });
        if (this.isCxone)
            this.usersDetails.scrollbarWidth = 8;
    }

    get challengeDetailsTitle(){
      const isShadowBoxing = this.challengeObj.ChallengeType === ChallengeTypes.ShadowBoxing;
      return isShadowBoxing ? 'Challenge_Details' : 'User_Details';
    }

    getColumns(challengeType: ChallengeTypeValues) {
        const defaultColumns = [
            {
                field: 'Name',
                headerName: this.getLocalizedMessage("User", "User"),
                minWidth: 75,
                cellRenderer: (param) => { return '<div class="d-flex align-items-center h-100">' + this.userRenderer(param) + '</div>' }
            }, {
                field: 'Place',
                headerName: this.getLocalizedMessage("Rank", "Rank"),
                minWidth: 75,
                valueGetter: (param) => {
                    return this.rankRenderer(param);
                },
                cellRenderer: (param) => { return '<div class="d-flex align-items-center h-100">' + param.value + '</div>' }
            }, {
                field: 'ViewScore',
                headerName: this.challengeObj.Metric,
                minWidth: 75,
                valueGetter: (param) => {
                    return this.MetricRenderer(param);
                },
                cellRenderer: (param) => { return '<div class="d-flex align-items-center h-100">' + (param.value === '-' ? (this.isCxone ? '-' : '---') : param.value) + '</div>' }
            },
        ];
        const isParticipantColumn = [
            {
                field: 'IsParticipant',
                headerName: this.getLocalizedMessage("Accepted_Invite", "Accepted Invite"),
                minWidth: 75,
                cellRenderer: (param) => { return '<div class="d-flex align-items-center h-100">' + this.inviteRenderer(param) + '</div>' },
                valueFormatter: (param) => {
                    return this.inviteRenderer(param);
                },
                filter: "agSetColumnFilter",
                filterParams: {
                    valueFormatter: param => {
                        return this.inviteRenderer(param);
                    }
                }
            }
        ]
        const CustomChallengeColumns = {
            [ChallengeTypes.BattleBoat]: [
                ...defaultColumns,
                {
                    field: 'ChallengeTypeName',
                    headerName: this.getLocalizedMessage("Type", "Type"),
                    minWidth: 75,
                    cellRenderer: (param) => `<div class="d-flex align-items-center h-100"> ${param.value} </div>`
                },
                {
                    field: 'Hits',
                    headerName: this.getLocalizedMessage("Hits", "Hits"),
                    minWidth: 75,
                    cellRenderer: (param) => `<div class="d-flex align-items-center h-100"> ${param.value} </div>`
                },
                {
                    field: 'SunkenShips',
                    headerName: this.getLocalizedMessage("SunkenShips", "SunkenShips"),
                    minWidth: 75,
                    cellRenderer: (param) => `<div class="d-flex align-items-center h-100"> ${param.value} </div>`
                },
                ...isParticipantColumn,
            ],
            [ChallengeTypes.ShadowBoxing]: [
                {
                    ...defaultColumns[0], // user column
                },
                {
                    field: 'GoalsAchieved',
                    headerName: this.getLocalizedMessage("GoalsAchieved", "Goals Achieved"),
                    minWidth: 75,
                    cellRenderer: (param) => { return '<div class="d-flex align-items-center h-100">' + param.value + '</div>' }
                },
                {
                    field: 'Metric',
                    headerName: this.getLocalizedMessage("Metric", "Metric"),
                    minWidth: 75,
                    cellRenderer: (param) => { return '<div class="d-flex align-items-center h-100">' + param.value + '</div>' }
                },
                {
                    field: 'DailyGoalMetricString',
                    headerName: this.getLocalizedMessage("DailyGoalMetric", "Daily goal"),
                    minWidth: 75,
                    cellRenderer: (param) => { return '<div class="d-flex align-items-center h-100">' + param.value + '</div>' }
                },
            ]
        }
        return CustomChallengeColumns[challengeType] || [...defaultColumns, ...isParticipantColumn];
    }

    getRankings(challengeType: ChallengeTypeValues) {
        // Determine if the challenge type is BattleBoats
        const isBattleBoat = challengeType === ChallengeTypes.BattleBoat;
        if (isBattleBoat) {
            // Localize the name for BattleBoats
            const battleBoatName = this.getLocalizedMessage("BattleBoats", "BattleBoats");
    
            // Create a mapping of players by their LoginID for easier reference
            const players = this.challengeObj.players?.reduce((acc, element) => {
                if (!element.LoginID) {
                    return acc;
                }
                return {
                    ...acc,
                    [element.LoginID]: element,
                };
            }, {}) || {};
    
            // Merge Rankings with player data and sort by Hits
            const battleBoatRanking = this.challengeObj.Rankings?.map(r => ({
                ...r,
                ...players[r.LoginID], // Combine player data with ranking
                ChallengeTypeName: battleBoatName, // Add the challenge type name
            })).sort((a, b) => b.Hits - a.Hits);
    
            // Process each ranking to ensure proper formatting of metrics
            return battleBoatRanking?.map((r, index) => ({
                ...r,
                Place: index + 1, // Assign the ranking position
                ViewScore: r.ViewScore ?? '-' ,
                Hits: r.Hits ?? 0, // Default to 0 if Hits are undefined
                SunkenShips: r.SunkenShips ?? 0, // Default to 0 if SunkenShips are undefined
            }));
        }
    
        // Determine if the challenge type is ShadowBoxing
        const isShadowBoxing = challengeType === ChallengeTypes.ShadowBoxing;
        if (isShadowBoxing && this.challengeObj.shadowBoxing) {
            const hp = this.challengeObj.shadowBoxing.Difficulty + 1; // Compute difficulty-related metric
    
            // Merge Rankings with ShadowBoxing-specific data
            const shadowBoxingRanking = this.challengeObj.Rankings?.map(r => ({
                ...r,
                DailyGoalMetricString: this.challengeObj.shadowBoxing.DailyGoalMetricString ||  '-',
                GoalsAchieved: `${this.challengeObj.shadowBoxing.Punch}/${hp}`, // Display progress against difficulty
                Metric: this.challengeObj.Metric ||  '-',
            }));
    
            return shadowBoxingRanking; // Return processed ShadowBoxing rankings
        }
    
        // Default case: return generic rankings if no specific challenge type is matched
        return this.challengeObj.Rankings?.map(r => ({
            ...r,
            ViewScore: r.ViewScore  ||  '-', 
            Hits: r.Hits ?? 0, // Default to 0 if Hits are undefined
            SunkenShips: r.SunkenShips ?? 0, // Default to 0 if SunkenShips are undefined
            DailyGoalMetricString: r.DailyGoalMetricString ||  '-',
            Metric: r.Metric ||  '-', 
        }));
    }

    loadGrid(): void {
        this.challengeObj.Rankings = this.getRankings(this.challengeObj.ChallengeType);
        const columns = this.getColumns(this.challengeObj.ChallengeType);
        if (this.agGrid?.api) {
            this.agGrid?.api.setGridOption('columnDefs', columns);
            this.agGrid?.api.setGridOption('rowData', this.challengeObj.Rankings);
        } else {
            this.usersDetails.columnDefs = columns;
            this.usersDetails.rowData = this.challengeObj.Rankings;
        }
    }

    //CELL RENDERER FUNCTIONS
    userRenderer(param): string {
        let imagePath = '';

        if (param.data.ImageID == 0 || param.data.ImageID == undefined || param.data.ImageID == -2) {
            imagePath = '/App_Themes/Standard/Images/blankProfile.png';
        } else {
            imagePath = '../Admin/ImageResizer.aspx?imgid=' + param.data.ImageID + '&w=32&h=32';
        }

        return '<span style="display:inline-flex;align-items:center;"><img src="' + imagePath + '" style="border-radius:50%; display:block; width:32px;"/><div style="margin-left:8px;">' + param.value + "</div></span>";
    }

    inviteRenderer(param): string {
        if (param.value) {
            return this.getLocalizedMessage("Yes", "Yes");
        } else {
            return this.getLocalizedMessage("Pending", "Pending");
        }
    }

    rankRenderer(param): string {
        let rank = "";

        if (param.data?.Place === -1 || !param.data?.ViewScore) {
            rank = "N/A";
        } else {
            rank = this.toOrdinalWithTranslation(param.data?.Place);
        }

        return rank;
    }

    toOrdinalWithTranslation(number: number): string {
        let TH = this.getLocalizedMessage("Th_Symbol", "th");
        let s = number.toString();

        // Negative and zero have no ordinal representation
        if (number < 1) return s;

        number %= 100;
        if (number >= 11 && number <= 13) {
            return s + TH;
        }

        switch (number % 10) {
            case 1: return s + this.getLocalizedMessage('St_Symbol', 'st');
            case 2: return s + this.getLocalizedMessage("Nd_Symbol", "nd");
            case 3: return s + this.getLocalizedMessage("Rd_Symbol", "rd");
            default: return s + TH;
        }
    }

    close(): void {
        this.activeModalService.close();
    }

    getLocalizedMessage(key: string, fallback: string): string {
        if (this.cs && this.cs.localizedStrings && this.cs.localizedStrings?.has(key)) {
            return this.cs.localizedStrings.get(key);
        }
        else { return fallback; }
    }

    getColumnSorted() {
        const colsId = this.agGrid?.api?.getColumnState().map(c => c.colId).filter(Boolean);
        const columns = this.agGrid?.api.getColumns().reduce((col, c) => ({ ...col, [c.getColId()]: c.getColDef().headerName }), {});
        const selectedColums = colsId.map(c => ({ field: c, headerName: columns[c] }));
        return selectedColums;
    }

    getDataSorted() {
        const selectedRowNodesWithSort = [];
        this.agGrid?.api.forEachNodeAfterFilterAndSort((node) => {
            selectedRowNodesWithSort.push({ ...node.data });
        });
        return selectedRowNodesWithSort;
    }

    async getUserDetailData(format: TypeFormats) {
        const ChallengeID = this.challengeObj.ChallengeID;
        const selectedColums = this.getColumnSorted();

        let data = this.getDataSorted();
        const challengeDetails = this.getLocalizedMessage('Challenge_Details', 'Challenge Details');
        const shortBody = this.isCxone ? challengeDetails : this.getLocalizedMessage(this.challengeDetailsTitle, 'Challenge Details');

        data = data.map(item => ({
            ...item,
            ViewScore: item.ViewScore === '-' ? (this.isCxone ? '-' : '---') : item.ViewScore
        }));

        const result = await this.challengesService.loadChallengesUserDetails({
            format,
            ChallengeID,
            selectedColums,
            data,
            startDate: this.challengeObj.StartDate,
            endDate: this.challengeObj.EndDate,
            shortBody,
        }, this.isCxone);
        return result;
    }

    MetricRenderer(param): string {
        let metric = "-";
        if (param.data?.ViewScore) {
            metric = param.data?.ViewScore;
        }
        return metric;
    }
}
